import { useContext, useMemo, useState } from "react";
import {
  FiArrowRight,
  FiCheck,
  FiMinus,
  FiPlus,
  FiShoppingBag,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";
import { productImageSrcSet } from "../utils/images";
import { findPromotion, promotionDiscount } from "../utils/promotions";
import { calculateStandardShipping } from "../utils/shipping";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import { money } from "./ProductCard";

const catalogById = new Map(
  productData.map((product) => [String(product.id), product])
);

const positiveQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
};

const firstImage = (product, item) => {
  const productImages = Array.isArray(product?.images)
    ? product.images.filter(Boolean)
    : [];
  const itemImages = Array.isArray(item?.images)
    ? item.images.filter(Boolean)
    : [];

  return productImages[0] || product?.image || itemImages[0] || item?.image || "";
};

const variantText = (item) => {
  const variant =
    item?.variant && typeof item.variant === "object"
      ? item.variant
      : {
          ...(item?.size ? { size: item.size } : {}),
          ...(item?.color ? { color: item.color } : {}),
        };

  return Object.entries(variant)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => ({
      label: key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, (letter) => letter.toUpperCase()),
      value,
    }));
};

const resolveCartLine = (item, index) => {
  const id = item?.id ?? item?.productId;
  const catalogProduct = id === undefined || id === null
    ? null
    : catalogById.get(String(id)) || null;
  const product = catalogProduct || item || {};
  const priceOptions = [item?.price, catalogProduct?.price].map(Number);
  const price = priceOptions.find(Number.isFinite) || 0;
  const quantity = positiveQuantity(item?.quantity);
  const stockOptions = [catalogProduct?.stock, item?.stock].map(Number);
  const stock = stockOptions.find(Number.isFinite);

  return {
    id,
    lineKey: item?.lineKey || (id !== undefined && id !== null ? String(id) : `missing-${index}`),
    target: item?.lineKey || id,
    product,
    missingFromCatalog: !catalogProduct,
    name: product.name || "Unavailable pantry item",
    slug: product.slug || item?.slug || "",
    image: firstImage(catalogProduct, item),
    quantity,
    price,
    lineTotal: price * quantity,
    stock: Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : null,
    variants: variantText(item),
  };
};

function CartPage() {
  const {
    cartItems,
    clearCart,
    removeFromCart,
    updateQuantity,
  } = useContext(CartContext);
  const [promoCode, setPromoCode] = useState("");
  const [promoFeedback, setPromoFeedback] = useState(null);
  const [appliedPromotion, setAppliedPromotion] = useState(null);

  const lines = useMemo(
    () =>
      (Array.isArray(cartItems) ? cartItems : [])
        .filter((item) => item && typeof item === "object")
        .map(resolveCartLine),
    [cartItems]
  );
  const purchasableLines = useMemo(
    () => lines.filter((line) => !line.missingFromCatalog),
    [lines]
  );
  const cartItemCount = useMemo(
    () => lines.reduce((total, line) => total + line.quantity, 0),
    [lines]
  );

  const subtotal = useMemo(
    () =>
      purchasableLines.reduce(
        (total, line) => total + line.lineTotal,
        0
      ),
    [purchasableLines]
  );
  const itemCount = useMemo(
    () =>
      purchasableLines.reduce((total, line) => total + line.quantity, 0),
    [purchasableLines]
  );

  const shippingConfig = siteConfig.shipping || {};
  const freeShippingThreshold = Number(
    shippingConfig.freeShippingThreshold ?? shippingConfig.freeThreshold
  );
  const hasFreeShippingThreshold =
    Number.isFinite(freeShippingThreshold) && freeShippingThreshold > 0;
  const qualifiesForFreeShipping =
    hasFreeShippingThreshold && subtotal >= freeShippingThreshold;
  const shippingCost = calculateStandardShipping({
    itemCount,
    subtotal,
    shipping: shippingConfig,
  });
  const discount = promotionDiscount(appliedPromotion, subtotal);
  const estimatedTotal = subtotal - discount + shippingCost;
  const amountUntilFreeShipping = hasFreeShippingThreshold
    ? Math.max(0, freeShippingThreshold - subtotal)
    : 0;
  const shippingProgress = hasFreeShippingThreshold
    ? Math.min(100, (subtotal / freeShippingThreshold) * 100)
    : 100;

  const currency = shippingConfig.currency || "USD";
  const configuredMoney = useMemo(
    () =>
      currency === "USD"
        ? money
        : new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
          }),
    [currency]
  );

  const changeQuantity = (line, nextQuantity) => {
    if (line.target === undefined || line.target === null) return;
    const maximum = Number.isFinite(line.stock) ? line.stock : 99;
    const next = Math.min(maximum, Math.max(1, nextQuantity));
    updateQuantity(line.target, next);
  };

  const removeLine = (line) => {
    if (line.target === undefined || line.target === null) return;
    removeFromCart(line.target);
  };

  const submitPromo = (event) => {
    event.preventDefault();
    const normalizedCode = promoCode.trim().toUpperCase();

    const promotion = findPromotion(normalizedCode);

    if (!promotion) {
      setPromoFeedback({
        type: "error",
        message: "That promo code is not available for this demo cart.",
      });
      return;
    }

    if (subtotal < Number(promotion.minimumSubtotal || 0)) {
      setPromoFeedback({
        type: "error",
        message: `${promotion.code} applies to orders of ${configuredMoney.format(
          Number(promotion.minimumSubtotal)
        )} or more.`,
      });
      return;
    }

    setPromoCode(normalizedCode);
    setAppliedPromotion(promotion);
    setPromoFeedback({
      type: "success",
      message: `${promotion.label} applied and carried into checkout.`,
    });
  };

  if (lines.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <PageMeta title="Shopping cart" noIndex />
        <section className="cart-page__empty-card">
          <span className="cart-page__empty-icon" aria-hidden="true">
            <FiShoppingBag />
          </span>
          <p className="cart-page__eyebrow">Your pantry edit</p>
          <h1>Your cart is waiting.</h1>
          <p>
            Browse small-batch pasta, bright preserves, fragrant oils, and a few
            sweet things for the table.
          </p>
          <Link className="cart-page__primary-link" to="/shop">
            Explore the pantry
            <FiArrowRight aria-hidden="true" />
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <PageMeta title="Shopping cart" noIndex />
      <header className="cart-page__header">
        <p className="cart-page__eyebrow">Your pantry edit</p>
        <h1>Your shopping cart</h1>
        <p>
          {cartItemCount} {cartItemCount === 1 ? "item" : "items"} selected
          {cartItemCount === itemCount
            ? " and ready for the table."
            : `; ${cartItemCount - itemCount} unavailable selection is excluded from checkout.`}
        </p>
      </header>

      {hasFreeShippingThreshold && (
        <section className="cart-page__shipping-progress" aria-label="Free shipping progress">
          <div className="cart-page__shipping-copy">
            <FiTruck aria-hidden="true" />
            <p>
              {qualifiesForFreeShipping ? (
                <>
                  <strong>Free standard shipping unlocked.</strong> We will pack
                  everything with care.
                </>
              ) : (
                <>
                  Add <strong>{configuredMoney.format(amountUntilFreeShipping)}</strong>
                  {" "}more for free standard shipping.
                </>
              )}
            </p>
            <span>{Math.round(shippingProgress)}%</span>
          </div>
          <div
            className="cart-page__progress-track"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={Math.round(shippingProgress)}
          >
            <span style={{ width: `${shippingProgress}%` }} />
          </div>
        </section>
      )}

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-labelledby="cart-items-title">
          <div className="cart-page__items-heading">
            <h2 id="cart-items-title">Items</h2>
            <button type="button" onClick={clearCart}>
              Clear cart
            </button>
          </div>

          <div className="cart-page__column-labels" aria-hidden="true">
            <span>Product</span>
            <span>Quantity</span>
            <span>Total</span>
          </div>

          <div className="cart-page__item-list">
            {lines.map((line) => {
              const productPath = line.slug
                ? `/product/${line.slug}`
                : line.id !== undefined && line.id !== null
                ? `/shopDetails/${line.id}`
                : "/shop";
              const canIncrease =
                line.stock === null || line.quantity < line.stock;

              return (
                <article className="cart-line" key={line.lineKey}>
                  <Link className="cart-line__image" to={productPath} tabIndex={line.image ? 0 : -1}>
                    {line.image ? (
                      <img
                        src={line.image}
                        srcSet={productImageSrcSet(line.image)}
                        sizes="(max-width: 700px) 96px, 140px"
                        alt={line.name}
                        width="1200"
                        height="1200"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span>Image unavailable</span>
                    )}
                  </Link>

                  <div className="cart-line__details">
                    <p className="cart-line__category">
                      {line.product.category || "Italian pantry"}
                    </p>
                    <h3>
                      <Link to={productPath}>{line.name}</Link>
                    </h3>
                    <p className="cart-line__unit">{line.product.unit || "Pantry selection"}</p>
                    {line.variants.length > 0 && (
                      <dl className="cart-line__variants">
                        {line.variants.map((variant) => (
                          <div key={`${variant.label}-${variant.value}`}>
                            <dt>{variant.label}:</dt>
                            <dd>{variant.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {line.missingFromCatalog && (
                      <p className="cart-line__availability">
                        This item is no longer listed and is not included in order
                        totals. Remove it before completing your cart.
                      </p>
                    )}
                    <button
                      className="cart-line__remove cart-line__remove--mobile"
                      type="button"
                      onClick={() => removeLine(line)}
                      disabled={line.target === undefined || line.target === null}
                    >
                      <FiTrash2 aria-hidden="true" />
                      Remove
                    </button>
                  </div>

                  <div className="cart-line__quantity-wrap">
                    <span className="cart-line__mobile-label">Quantity</span>
                    <div className="cart-line__quantity">
                      <button
                        type="button"
                        onClick={() => changeQuantity(line, line.quantity - 1)}
                        disabled={line.quantity <= 1}
                        aria-label={`Decrease quantity of ${line.name}`}
                      >
                        <FiMinus aria-hidden="true" />
                      </button>
                      <output aria-label={`Quantity of ${line.name}`}>{line.quantity}</output>
                      <button
                        type="button"
                        onClick={() => changeQuantity(line, line.quantity + 1)}
                        disabled={!canIncrease}
                        aria-label={`Increase quantity of ${line.name}`}
                      >
                        <FiPlus aria-hidden="true" />
                      </button>
                    </div>
                    {line.stock !== null && line.stock <= 8 && (
                      <small>{line.stock} available</small>
                    )}
                  </div>

                  <div className="cart-line__total">
                    <span className="cart-line__mobile-label">Line total</span>
                    <strong>{configuredMoney.format(line.lineTotal)}</strong>
                    <small>{configuredMoney.format(line.price)} each</small>
                    <button
                      className="cart-line__remove cart-line__remove--desktop"
                      type="button"
                      onClick={() => removeLine(line)}
                      disabled={line.target === undefined || line.target === null}
                      aria-label={`Remove ${line.name} from cart`}
                    >
                      <FiTrash2 aria-hidden="true" />
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          <Link className="cart-page__continue" to="/shop">
            <span aria-hidden="true">←</span>
            Continue shopping
          </Link>
        </section>

        <aside className="cart-page__summary" aria-labelledby="cart-summary-title">
          <div className="cart-page__summary-card">
            <p className="cart-page__eyebrow">Order summary</p>
            <h2 id="cart-summary-title">Almost yours</h2>

            <div className="cart-page__summary-rows">
              <div>
                <span>Subtotal</span>
                <strong>{configuredMoney.format(subtotal)}</strong>
              </div>
              {discount > 0 && (
                <div>
                  <span>Promo · {appliedPromotion.code}</span>
                  <strong>−{configuredMoney.format(discount)}</strong>
                </div>
              )}
              <div>
                <span>{shippingConfig.standard?.label || "Standard shipping"}</span>
                <strong>
                  {qualifiesForFreeShipping ? "Free" : configuredMoney.format(shippingCost)}
                </strong>
              </div>
              <div className="cart-page__summary-total">
                <span>Estimated total</span>
                <strong>{configuredMoney.format(estimatedTotal)}</strong>
              </div>
            </div>

            <p className="cart-page__tax-note">
              Taxes are not calculated in this front-end demo. Shipping is based
              on the current cart and may update with your delivery address.
            </p>

            <form className="cart-page__promo" onSubmit={submitPromo} noValidate>
              <label htmlFor="cart-promo-code">Promo code</label>
              <div>
                <input
                  id="cart-promo-code"
                  type="text"
                  value={promoCode}
                  onChange={(event) => {
                    setPromoCode(event.target.value);
                    setPromoFeedback(null);
                    setAppliedPromotion(null);
                  }}
                  placeholder="Enter code"
                  autoComplete="off"
                  aria-describedby="cart-promo-feedback"
                />
                <button type="submit">Apply</button>
              </div>
              <p
                id="cart-promo-feedback"
                className={promoFeedback ? `is-${promoFeedback.type}` : ""}
                role="status"
                aria-live="polite"
              >
                {promoFeedback?.type === "success" && <FiCheck aria-hidden="true" />}
                {promoFeedback?.message || "Demo code: PANTRY10 on orders of $50 or more."}
              </p>
            </form>

            {itemCount > 0 ? (
              <Link
                className="cart-page__checkout"
                to="/checkout"
                state={appliedPromotion ? { promotionCode: appliedPromotion.code } : undefined}
              >
                Continue to checkout
                <FiArrowRight aria-hidden="true" />
              </Link>
            ) : (
              <p className="cart-page__availability" role="status">
                Remove unavailable items, then add a current product to continue.
              </p>
            )}

            <ul className="cart-page__trust-list">
              <li>Secure, encrypted checkout</li>
              <li>Packed in {shippingConfig.processingTime || "1–2 business days"}</li>
              <li>{siteConfig.returns?.guarantee || "Satisfaction guarantee"}</li>
            </ul>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default CartPage;
